import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from './config/authConfig';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SystemProvider } from './contexts/SystemContext';
import { apiClient } from './services/api';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import AccessDenied from './pages/AccessDenied';
import Dashboard from './pages/Dashboard';
import Analytics from './pages/Analytics';

import Prompts from './pages/Prompts';
import SystemStatus from './pages/SystemStatus';

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Component to connect API client with auth context
const ApiClientProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getAccessToken, logout } = useAuth();

  useEffect(() => {
    // Set token provider for API client
    apiClient.setTokenProvider(getAccessToken);
  }, [getAccessToken]);

  return <>{children}</>;
};

// Main app routes component
const AppRoutes: React.FC = () => {
  const { logout } = useAuth();

  return (
    <ApiClientProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/access-denied" element={<AccessDenied />} />
        
        {/* Protected routes with shared SystemProvider */}
        <Route path="/*" element={
          <ProtectedRoute requireAdmin={true}>
            <SystemProvider>
              <Layout onLogout={logout}>
                <Routes>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/prompts" element={<Prompts />} />
                  <Route path="/system" element={<SystemStatus />} />
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </Layout>
            </SystemProvider>
          </ProtectedRoute>
        } />
      </Routes>
    </ApiClientProvider>
  );
};

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <Router>
          <div className="min-h-screen bg-gray-50">
            <AppRoutes />
          </div>
        </Router>
      </AuthProvider>
    </MsalProvider>
  );
}

export default App;
